// Copyright (c) 2022 by Move Closer

import { RawLocation } from 'vue-router'
import { TranslateResult } from 'vue-i18n'

import { Description, HasIdentifier, Related } from '../../shared/related'

import { ContentData } from './content'
import { BenefitProgram } from '../../profile'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface DescriptionOfLink extends Description, HasIdentifier {
  title: string
  url: string
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export interface DescriptionOfNavigation<CanHaveEmptyTarget extends boolean = false> extends Description {
  links: NavigationRawItem[]
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface Link<CanHaveEmptyTarget extends boolean = false> {
  isExternal?: boolean
  label?: string | TranslateResult
  tabTarget?: TabOption
  target: CanHaveEmptyTarget extends true ? RawLocation | null : RawLocation
  title?: string
  snippet?: ContentData
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export type LinkData = DescriptionOfLink

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export interface Navigation<CanHaveEmptyTarget extends boolean = false> {
  links: NavigationItem<CanHaveEmptyTarget>[]
  name: string
  slot: string
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export interface NavigationItem<CanHaveEmptyTarget extends boolean = false> extends Link<CanHaveEmptyTarget>, Description {
  additionalClass?: string
  children?: NavigationItem[]
  icon?: string
  secondaryLabel?: string
  snippet?: ContentData
  badge?: string
  badgeExpiresAt?: string
  colorCode?: string
  loyalty?: BenefitProgram[]
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export interface NavigationRawItem {
  additionalClass?: string
  children: NavigationRawItem[]
  icon?: string
  label: string
  secondaryLabel?: string
  strategy?: OpenStrategy
  target: NavigationTarget | Related
  snippet?: Related
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export interface NavigationTarget {
  link: string
  type: Target
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export enum OpenStrategy {
  Blank = 'blank',
  Self = 'self'
}

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export enum TabOption {
  Blank = '_blank',
  Self = '_self'
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export enum Target {
  External = 'external',
  Internal = 'internal'
}
